import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Autocomplete, Checkbox, TextField } from '@mui/material';
import { debounce } from '@mui/material/utils';
import useTags from 'hooks/swr/useTags';
import { useState, useCallback, useMemo, type FC } from 'react';
import { useController, useFormContext } from 'react-hook-form';

const DEBOUNCE_DELAY = 300;
const PAGE_SIZE = 100;

interface Tag {
  id: number;
  name: string;
}

interface TagsAutocompleteProps {
  name: string;
  disabled?: boolean;
}

interface TagOptionProps extends React.HTMLAttributes<HTMLLIElement> {
  option: Tag;
  selected: boolean;
}

const TagOption: FC<TagOptionProps> = ({ option, selected, ...props }) => (
  <li {...props}>
    <Checkbox
      edge='start'
      icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
      checkedIcon={<CheckBoxIcon fontSize='small' />}
      style={{ marginRight: 4 }}
      checked={selected}
      aria-label={`Выбрать тег ${option.name}`}
    />
    {option.name}
  </li>
);

const TagsAutocomplete: FC<TagsAutocompleteProps> = ({ name, disabled }) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue, setDebouncedInputValue] = useState('');
  const formContext = useFormContext();

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: formContext.control,
    disabled,
  });

  const debouncedSetValue = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedInputValue(value);
      }, DEBOUNCE_DELAY),
    []
  );

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent | null, newValue: string, reason: string): void => {
      if (reason === 'reset') return;
      setInputValue(newValue);
      debouncedSetValue(newValue);
    },
    [debouncedSetValue]
  );

  const { data, isLoading } = useTags({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
    filterText: debouncedInputValue,
  });

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, value: Tag[] | null): void => {
      field.onChange(value ?? []);
    },
    [field]
  );

  return (
    <Autocomplete<Tag, true>
      fullWidth
      multiple
      autoComplete
      includeInputInList
      disableCloseOnSelect
      clearOnBlur
      loading={isLoading}
      options={data?.items ?? []}
      value={field.value ?? []}
      inputValue={inputValue}
      slotProps={{
        chip: { size: 'small' },
        popper: { 'aria-label': 'Список доступных тегов' },
      }}
      noOptionsText='Теги не найдены'
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x}
      onBlur={field.onBlur}
      onChange={handleChange}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField
          {...params}
          margin='normal'
          label='Теги'
          error={!!error}
          fullWidth
          helperText={error?.message}
          placeholder='Выберите тег'
          inputProps={{
            ...params.inputProps,
            'aria-label': 'Поиск и выбор тегов',
            'aria-description':
              'Введите текст для поиска тегов. Используйте стрелки для навигации по списку.',
          }}
        />
      )}
      renderOption={({ key, ...props }, option, { selected }) => (
        <TagOption key={key} {...props} option={option} selected={selected} />
      )}
    />
  );
};

export type { TagsAutocompleteProps };
export default TagsAutocomplete;
